import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { Mic, Play, Loader2, Sparkles, Share2, Download, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HOSTS = {
  Maya: { color: "#38BDF8", role: "AI Host" },
  Expert: { color: "#E2C275", role: "Our Expert" },
};

export default function PodcastPage({ initialSlug }) {
  const [posts, setPosts] = useState([]);
  const [active, setActive] = useState(null); // slug being viewed
  const [episodes, setEpisodes] = useState({}); // slug -> {transcript, audio_url}
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [shared, setShared] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/blog/posts`).then((r) => setPosts(r.data)).catch(() => {});
  }, []);

  const open = async (post) => {
    setActive(post.slug);
    setError(null);
    if (episodes[post.slug]) return;
    setLoading(true);
    try {
      const r = await axios.post(`${API}/podcasts/generate`, {
        slug: post.slug,
        title: post.title,
        body: post.body,
      });
      setEpisodes((e) => ({ ...e, [post.slug]: r.data }));
    } catch (err) {
      setError("Couldn't generate this episode. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initialSlug && posts.length) {
      const p = posts.find((x) => x.slug === initialSlug);
      if (p) open(p);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSlug, posts]);

  const post = posts.find((p) => p.slug === active);
  const ep = active ? episodes[active] : null;
  const audioSrc = ep ? `${process.env.REACT_APP_BACKEND_URL}${ep.audio_url}` : null;

  const share = async () => {
    if (!post || !audioSrc) return;
    const data = { title: `${post.title} — The Migration Podcast`, text: post.title, url: audioSrc };
    try {
      if (navigator.share) {
        await navigator.share(data);
        return;
      }
    } catch (e) {
      /* user cancelled or unsupported — fall through to copy */
    }
    try {
      await navigator.clipboard.writeText(audioSrc);
      setShared(true);
      setTimeout(() => setShared(false), 2000);
    } catch (e) {
      /* noop */
    }
  };

  if (active && post) {
    return (
      <div className="mx-auto max-w-lg px-4 pb-8 pt-4" data-testid="podcast-episode">
        <button
          data-testid="podcast-back"
          onClick={() => setActive(null)}
          className="text-xs font-medium text-slate-300"
        >
          ← All episodes
        </button>

        <div className="mt-3 overflow-hidden rounded-3xl border border-white/[0.08]">
          <div className="relative h-44 w-full overflow-hidden">
            <img src={post.image} alt={post.title} className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#07090E] via-[#07090E]/40 to-transparent" />
            <div className="absolute bottom-3 left-4 right-4">
              <p className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-amber-400/90">
                <Mic size={11} /> The Migration Podcast
              </p>
              <h1 className="mt-1 text-xl font-semibold leading-tight text-white" style={{ fontFamily: "'Syne', sans-serif" }}>
                {post.title}
              </h1>
            </div>
          </div>

          <div className="bg-[#0b0f18] p-4">
            <p className="text-[11px] text-slate-300">
              Hosted by <span className="text-sky-300">Maya (AI Host)</span> with{" "}
              <span className="text-amber-200">our expert</span>
            </p>

            {loading && (
              <div className="mt-5 flex flex-col items-center py-8 text-center" data-testid="podcast-loading">
                <div className="relative grid h-14 w-14 place-items-center">
                  <span className="absolute inset-0 animate-ping rounded-full bg-amber-300/20" />
                  <Sparkles className="text-amber-300" size={24} />
                </div>
                <p className="mt-4 text-sm text-slate-200">Generating your episode…</p>
                <p className="mt-1 text-[11px] text-slate-400">Writing the conversation and voicing it — about 20–30s.</p>
              </div>
            )}

            {error && <p className="mt-4 text-sm text-red-400" data-testid="podcast-error">{error}</p>}

            {ep && (
              <>
                <audio
                  ref={audioRef}
                  data-testid="podcast-audio"
                  controls
                  src={audioSrc}
                  className="mt-4 w-full"
                />
                <div className="mt-3 flex items-center gap-2">
                  <button
                    data-testid="podcast-share-btn"
                    onClick={share}
                    className="flex items-center gap-1.5 rounded-full border border-white/12 bg-white/[0.04] px-3.5 py-1.5 text-[12px] font-medium text-slate-200 transition-colors hover:border-amber-300/40 hover:text-white"
                  >
                    {shared ? <Check size={14} className="text-emerald-400" /> : <Share2 size={14} />}
                    {shared ? "Link copied" : "Share"}
                  </button>
                  <a
                    data-testid="podcast-download-btn"
                    href={audioSrc}
                    download={`${post.slug}.mp3`}
                    className="flex items-center gap-1.5 rounded-full border border-white/12 bg-white/[0.04] px-3.5 py-1.5 text-[12px] font-medium text-slate-200 transition-colors hover:border-amber-300/40 hover:text-white"
                  >
                    <Download size={14} /> Download
                  </a>
                </div>
                <div className="mt-5 space-y-3" data-testid="podcast-transcript">
                  {ep.transcript.map((line, i) => {
                    const h = HOSTS[line.speaker] || HOSTS.Maya;
                    return (
                      <div key={i} className="flex gap-3">
                        <div
                          className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full text-[11px] font-bold"
                          style={{ background: `${h.color}22`, color: h.color }}
                        >
                          {line.speaker[0]}
                        </div>
                        <div>
                          <p className="text-[11px] font-semibold" style={{ color: h.color }}>{h.role}</p>
                          <p className="text-[13px] leading-snug text-slate-200">{line.text}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-lg px-4 pb-8 pt-4" data-testid="podcast-page">
      <h1 className="flex items-center gap-2 text-2xl font-semibold text-white" style={{ fontFamily: "'Syne', sans-serif" }}>
        <Mic size={22} className="text-amber-300" /> The Migration Podcast
      </h1>
      <p className="mt-1 text-sm text-slate-300">
        Every article, reimagined as a conversation between our AI host and our expert.
      </p>

      <div className="mt-5 space-y-4">
        {posts.map((p) => (
          <button
            key={p.slug}
            data-testid={`podcast-card-${p.slug}`}
            onClick={() => open(p)}
            className="group flex w-full gap-3 overflow-hidden rounded-2xl border border-white/[0.07] bg-white/[0.03] p-3 text-left transition-colors hover:border-amber-300/30"
          >
            <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-xl">
              <img src={p.image} alt={p.title} className="h-full w-full object-cover" />
              <div className="absolute inset-0 grid place-items-center bg-black/30">
                <span className="grid h-8 w-8 place-items-center rounded-full bg-amber-300 text-[#07090E]">
                  <Play size={14} className="ml-0.5" fill="currentColor" />
                </span>
              </div>
            </div>
            <div className="flex-1">
              <p className="font-mono text-[10px] uppercase tracking-wider text-amber-400/80">Episode</p>
              <h2 className="mt-0.5 text-sm font-semibold leading-snug text-white">{p.title}</h2>
              <p className="mt-1 line-clamp-2 text-[12px] leading-snug text-slate-300">{p.excerpt}</p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
